import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X, Loader2, BedDouble, Bath, Car, ExternalLink, HelpCircle, UserCheck, Globe, Phone, Lock,
} from 'lucide-react';
import api from '../../../api';
import { formatMoney, preferenceParts } from '../utils/buyerPreferenceSummary';
import { OwnerContactList } from '../../dashboard/components/RelistingOwnerContacts';
import { MatchLevelChip, NextAction, ReasonChips, scoreTitle } from './MatchLevel';

/**
 * "Match properties" for one buyer: one list of every property we hold that
 * fits, strongest match first, whatever stage it is at (Christian Gravias,
 * DOMA 13460623858: "all relevant properties appearing together in one ranked
 * list, strongest matches first").
 *
 * Each card says what kind of opportunity it is (For Sale, Off-Market,
 * Appraisal, Seller Lead, Withdrawn, Re-listing Opportunity, Public Property
 * Data), so the agent knows at a glance whether they can introduce the buyer
 * now or need the owner's selling intentions first. It shows the Property
 * Match Score and the data confidence separately, why it matches, the Collings
 * agent who holds it (to call about a deal) and the next step.
 *
 * Every agent sees every agent's stock here. A seller lead's or Database row's
 * owner is named only to the agent who holds it; anyone else is told whose
 * it is. Prices are today's figures only; a past sale shows as "Last sold".
 */

// Opportunity colours, from ready to introduce (green) to owner unknown (grey).
const OPPORTUNITY_CLS = {
  for_sale: 'bg-emerald-100 text-emerald-800',
  off_market: 'bg-teal-100 text-teal-800',
  appraisal: 'bg-violet-100 text-violet-800',
  seller_lead: 'bg-indigo-100 text-indigo-800',
  withdrawn: 'bg-amber-100 text-amber-800',
  relisting: 'bg-orange-100 text-orange-800',
  public_data: 'bg-gray-100 text-gray-700',
};

const LEAD_STAGE_PARAM = { New: 'new', Connected: 'connected', 'Long Term': 'long_term' };

// Where a card opens. Another agent's seller lead or Database row is not
// theirs to open, so it has no link.
const linkFor = (item) => {
  if (item.owner_hidden) return null;
  if (item.kind === 'property') return `/properties/${item.id}`;
  if (item.kind === 'lead') {
    const stage = LEAD_STAGE_PARAM[item.pipeline_stage];
    return `/prospects?view=list${stage ? `&stage=${stage}` : ''}`;
  }
  if (item.kind === 'public') return `/public-properties/${item.id}`;
  return '/prospecting/relisting-opportunities';
};

// A re-listing's guide is the agency's own wording ("$450,000 - $495,000");
// anything else is the price.
const priceText = (item) => {
  if (item.kind === 'relisting' && item.price_label) return item.price_label;
  if (item.price == null) return '';
  return formatMoney(item.price);
};

const lastSoldText = (sold) => {
  if (!sold?.price) return '';
  const year = sold.date ? ` (${sold.date.slice(0, 4)})` : '';
  return `Last sold ${formatMoney(sold.price)}${year}`;
};

// Only a web address opens: listing_url comes from Homer's data, and an href
// must never carry a javascript: or data: URL.
const webUrl = (url) => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
};

const telHref = (phone) => {
  const digits = (phone || '').replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : null;
};

// "Beds: Database · Price: Owner's expectation, Agent update, 5 Nov 2026"
const sourcesTitle = (sources) => {
  const entries = Object.entries(sources || {});
  if (!entries.length) return undefined;
  const names = { property_type: 'Type', beds: 'Beds', baths: 'Baths', cars: 'Parking', price: 'Price' };
  return entries.map(([k, v]) => `${names[k] || k}: ${v}`).join(' · ');
};

const OpportunityChip = ({ item }) => (
  <span
    className={`rounded-full text-[11px] font-semibold px-2 py-0.5 whitespace-nowrap ${OPPORTUNITY_CLS[item.opportunity] || OPPORTUNITY_CLS.public_data}`}
    title={item.opportunity_note}
  >
    {item.opportunity_label}
  </span>
);

const AgentLine = ({ item }) => {
  // An older backend sent the Database owner's agent as a plain name.
  const agent = typeof item.agent === 'string' ? (item.agent ? { name: item.agent } : null) : item.agent;
  const tel = telHref(agent?.phone);
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
      {agent ? (
        <span className="inline-flex items-center gap-1">
          <span className="text-gray-400">Agent</span>
          <span className="font-medium text-gray-800">{agent.name}</span>
          {tel && (
            <a href={tel} className="inline-flex items-center gap-0.5 text-[#0e7490] hover:underline">
              <Phone className="w-3 h-3" /> {agent.phone}
            </a>
          )}
        </span>
      ) : (
        <span className="text-gray-400">No Collings agent holds this property yet</span>
      )}
      {item.pipeline_stage && <span className="text-gray-400">· {item.pipeline_stage}</span>}
      {item.owner_hidden ? (
        <span className="inline-flex items-center gap-1 text-gray-400">
          <Lock className="w-3 h-3" /> Owner details with {agent?.name || 'the agent who holds it'}
        </span>
      ) : (item.kind !== 'property' && item.name) ? (
        <span><span className="text-gray-400">Owner</span> {item.name}</span>
      ) : null}
    </div>
  );
};

const MatchCard = ({ item }) => {
  const [ownersOpen, setOwnersOpen] = useState(false);
  const listingHref = item.kind === 'relisting' ? webUrl(item.listing_url) : null;
  const href = listingHref ? null : linkFor(item);
  const owners = item.owner_contacts || [];
  const titleCls = 'font-semibold text-gray-900 hover:text-[#0e7490] hover:underline';
  const address = item.address || item.name;
  const sold = lastSoldText(item.last_sold);
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/60 hover:bg-white hover:border-[#55C5D0]/50 hover:shadow-sm transition-all p-3">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="min-w-0 flex flex-wrap items-center gap-2">
          <OpportunityChip item={item} />
          {listingHref ? (
            <a href={listingHref} target="_blank" rel="noopener noreferrer" className={titleCls}>
              {address}<ExternalLink className="inline w-3 h-3 ml-1 text-gray-400" />
            </a>
          ) : href ? (
            <Link to={href} target="_blank" rel="noopener noreferrer" className={titleCls}>
              {address}<ExternalLink className="inline w-3 h-3 ml-1 text-gray-400" />
            </Link>
          ) : (
            <span className="font-semibold text-gray-900">{address}</span>
          )}
        </div>
        <span className="inline-flex flex-wrap items-baseline gap-x-3 gap-y-1">
          {priceText(item) && (
            <span className="text-sm font-semibold text-gray-700" title={item.fact_sources?.price}>
              {priceText(item)}
            </span>
          )}
          {item.match_score != null && (
            <span className="text-sm font-bold text-[#0e7490]" title={scoreTitle(item.score_parts)}>
              {item.match_score} match
            </span>
          )}
          <MatchLevelChip match={item} />
        </span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500" title={sourcesTitle(item.fact_sources)}>
        {item.property_type && <span className="capitalize">{item.property_type}</span>}
        {item.beds != null && <span className="inline-flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" />{item.beds}</span>}
        {item.baths != null && <span className="inline-flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{item.baths}</span>}
        {item.cars != null && <span className="inline-flex items-center gap-1"><Car className="w-3.5 h-3.5" />{item.cars}</span>}
        {sold && <span className="text-gray-400">{sold}</span>}
        {item.agency && <span>{item.agency}</span>}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-gray-400 mr-0.5">Why it matches</span>
        <ReasonChips match={item} />
        {owners.length > 0 ? (
          <button
            type="button"
            aria-expanded={ownersOpen}
            onClick={() => setOwnersOpen((o) => !o)}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold px-2 py-0.5 hover:bg-emerald-100"
          >
            <UserCheck className="w-3 h-3" /> {ownersOpen ? 'Hide owner details' : 'Owner details'}
          </button>
        ) : item.has_owner && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium px-2 py-0.5"
            title="An owner is on file in the Database for this address.">
            <UserCheck className="w-3 h-3" /> Owner in Database
          </span>
        )}
        {item.details_unknown && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 text-[11px] px-2 py-0.5"
            title="Some details are not on file for this property, so it matched on what is known.">
            <HelpCircle className="w-3 h-3" /> Matched on known details
          </span>
        )}
      </div>
      <AgentLine item={item} />
      <NextAction match={item} className="mt-1.5" />
      {ownersOpen && (
        <div className="mt-2 rounded-lg border border-teal-100 bg-teal-50/40 px-3 py-2">
          <OwnerContactList contacts={owners} />
        </div>
      )}
    </div>
  );
};

const BuyerMatchPropertiesModal = ({ person, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [data, setData] = useState(null);
  const [only, setOnly] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('api/buyer-portal/analytics/find-buyers/match-properties/', {
          params: {
            ...(person.contact_id ? { contact_id: person.contact_id } : {}),
            ...(person.buyer_id ? { buyer_id: person.buyer_id } : {}),
          },
        });
        if (!cancelled) setData(res.data);
      } catch (error) {
        console.error('Failed to match properties:', error);
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [person.contact_id, person.buyer_id]);

  // The ranked list; an older backend sends only groups, so flatten them.
  const items = useMemo(() => {
    if (Array.isArray(data?.items)) return data.items;
    return (data?.groups || []).flatMap((g) => g.items)
      .sort((a, b) => (b.match_score || 0) - (a.match_score || 0));
  }, [data]);
  const opportunities = (data?.opportunities || []).filter((o) => data?.counts?.[o.key]);
  const shown = only === 'all' ? items : items.filter((i) => i.opportunity === only);
  const prefs = preferenceParts(data?.preferences);

  const filterCls = (active) => `rounded-full text-xs font-medium px-2.5 py-1 border transition-colors ${
    active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
  }`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onMouseDown={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Matching properties for ${person.name}`}
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-100">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-gray-900">Matching properties · {person.name}</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {prefs.length ? `Wants ${prefs.join(' · ')}` : 'Every property we hold, best match first.'}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {opportunities.length > 1 && (
          <div className="flex flex-wrap gap-1.5 px-5 pt-3" role="group" aria-label="Show opportunities">
            <button type="button" className={filterCls(only === 'all')} onClick={() => setOnly('all')}>
              All ({items.length})
            </button>
            {opportunities.map((o) => (
              <button key={o.key} type="button" title={o.note} className={filterCls(only === o.key)}
                onClick={() => setOnly(o.key)}>
                {o.label} ({data.counts[o.key]})
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : failed ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              We could not match properties just now. Close this and try again.
            </div>
          ) : !data?.preferences ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              This buyer has no buying preferences saved yet, so there is nothing to match on.
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              Nothing we hold fits this buyer right now.
            </div>
          ) : (
            <>
              {data.broad_city && (
                <div className="flex items-start gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                  <Globe className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                  <span>
                    This buyer named only {data.broad_city}, not a suburb, so these are broad matches on
                    budget, bedrooms and property type. Ask them which areas they like to narrow it down.
                  </span>
                </div>
              )}
              {shown.map((item) => (
                <MatchCard key={`${item.kind}-${item.id}`} item={item} />
              ))}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <span className="text-sm text-gray-500">
            {loading || failed ? '' : `${items.length} matching ${items.length === 1 ? 'property' : 'properties'}, strongest first`}
          </span>
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuyerMatchPropertiesModal;
